import { Center, Loader } from '@mantine/core';
import { lazy, Suspense } from 'react';
import { useSelector } from 'react-redux';
import { Route, Routes } from 'react-router-dom';
import { ScrollToTop } from '../components/ScrollToTop';

function PageFallback() {
  return (
    <Center h="60vh">
      <Loader color="orange" size="md" />
    </Center>
  );
}

import { useUserConditionalRender } from '../hooks/use-user-conditonal-render';
import { UserRole } from '../models/user';
import { RootState } from '../providers/store';
import { AppRoutePaths } from '../utils/enums';

const ActivityList = lazy(() =>
  import('../pages/ActivityList').then((m) => ({ default: m.ActivityList })),
);
const ActivityListExport = lazy(() =>
  import('../pages/ActivityListExport').then((m) => ({
    default: m.ActivityListExport,
  })),
);
const ArtProduction = lazy(() =>
  import('../pages/ArtProduction').then((m) => ({ default: m.ArtProduction })),
);
const ArtReport = lazy(() =>
  import('../pages/ArtReport').then((m) => ({ default: m.ArtReport })),
);
const BillCreate = lazy(() =>
  import('../pages/BillCreate').then((m) => ({ default: m.BillCreate })),
);
const BillEdit = lazy(() =>
  import('../pages/BillEdit').then((m) => ({ default: m.BillEdit })),
);
const BillList = lazy(() =>
  import('../pages/BillList').then((m) => ({ default: m.BillList })),
);
const BudgetList = lazy(() => import('../pages/BudgetList'));
const ChatMonitor = lazy(() =>
  import('../pages/ChatMonitor').then((m) => ({ default: m.ChatMonitor })),
);
const CompanyCreate = lazy(() =>
  import('../pages/CompanyCreate').then((m) => ({ default: m.CompanyCreate })),
);
const CompanyEdit = lazy(() =>
  import('../pages/CompanyEdit').then((m) => ({ default: m.CompanyEdit })),
);
const CompanyList = lazy(() =>
  import('../pages/CompanyList').then((m) => ({ default: m.CompanyList })),
);
const CompanyXBZList = lazy(() =>
  import('../pages/CompanyXbz').then((m) => ({ default: m.CompanyXBZList })),
);
const ConfirmBudget = lazy(() =>
  import('../pages/ConfirmBudget').then((m) => ({ default: m.ConfirmBudget })),
);
const EventReport = lazy(() =>
  import('../pages/EventReport').then((m) => ({ default: m.EventReport })),
);
const FinancialInvoice = lazy(() =>
  import('../pages/FinancialInvoice').then((m) => ({
    default: m.FinancialInvoice,
  })),
);
const FinancialNFControl = lazy(() =>
  import('../pages/FinancialNFControl').then((m) => ({
    default: m.FinancialNFControl,
  })),
);
const FinancialPayment = lazy(() =>
  import('../pages/FinancialPayment').then((m) => ({
    default: m.FinancialPayment,
  })),
);
const HandlingReport = lazy(() =>
  import('../pages/HandlingReport').then((m) => ({
    default: m.HandlingReport,
  })),
);
const Home = lazy(() =>
  import('../pages/Home').then((m) => ({ default: m.Home })),
);
const LayoutList = lazy(() =>
  import('../pages/LayoutList').then((m) => ({ default: m.LayoutList })),
);
const Login = lazy(() =>
  import('../pages/Login').then((m) => ({ default: m.Login })),
);
const CreateOption = lazy(() =>
  import('../pages/OptionCreate').then((m) => ({ default: m.CreateOption })),
);
const EditOption = lazy(() =>
  import('../pages/OptionEdit').then((m) => ({ default: m.EditOption })),
);
const OptionList = lazy(() =>
  import('../pages/OptionList').then((m) => ({ default: m.OptionList })),
);
const OrderApprovalDeadline = lazy(() =>
  import('../pages/OrderApprovalDeadline').then((m) => ({
    default: m.OrderApprovalDeadline,
  })),
);
const OrderCancel = lazy(() =>
  import('../pages/OrderCancel').then((m) => ({ default: m.OrderCancel })),
);
const OrderChangeRequests = lazy(() =>
  import('../pages/OrderChangeRequests').then((m) => ({
    default: m.OrderChangeRequests,
  })),
);
const CreateOrder = lazy(() =>
  import('../pages/OrderCreate').then((m) => ({ default: m.CreateOrder })),
);
const OrderCreateByXBZ = lazy(() =>
  import('../pages/OrderCreateByXBZ').then((m) => ({
    default: m.OrderCreateByXBZ,
  })),
);
const OrderCreateKit = lazy(() =>
  import('../pages/OrderCreateKit').then((m) => ({
    default: m.OrderCreateKit,
  })),
);
const OrderDefect = lazy(() =>
  import('../pages/OrderDefect').then((m) => ({ default: m.OrderDefect })),
);
const OrderDispatch = lazy(() =>
  import('../pages/OrderDispatch').then((m) => ({ default: m.OrderDispatch })),
);
const EditOrder = lazy(() =>
  import('../pages/OrderEdit').then((m) => ({ default: m.EditOrder })),
);
const OrderEventCreate = lazy(() =>
  import('../pages/OrderEventCreate').then((m) => ({
    default: m.OrderEventCreate,
  })),
);
const OrderEventEdit = lazy(() =>
  import('../pages/OrderEventEdit').then((m) => ({
    default: m.OrderEventEdit,
  })),
);
const OrderEventList = lazy(() =>
  import('../pages/OrderEventList').then((m) => ({
    default: m.OrderEventList,
  })),
);
const OrderFileReceiptsApproval = lazy(() =>
  import('../pages/OrderFileReceiptsApproval').then((m) => ({
    default: m.OrderFileReceiptsApproval,
  })),
);
const OrderFirstContact = lazy(() =>
  import('../pages/OrderFirstContact').then((m) => ({
    default: m.OrderFirstContact,
  })),
);
const OrderHandling = lazy(() =>
  import('../pages/OrderHandling').then((m) => ({ default: m.OrderHandling })),
);
const OrderHistoric = lazy(() =>
  import('../pages/OrderHistoric').then((m) => ({ default: m.OrderHistoric })),
);
const OrderLateList = lazy(() =>
  import('../pages/OrderLateList').then((m) => ({ default: m.OrderLateList })),
);
const OrderList = lazy(() =>
  import('../pages/OrderList').then((m) => ({ default: m.OrderList })),
);
const OrderListFinancialApproval = lazy(() =>
  import('../pages/OrderListFinancialApproval').then((m) => ({
    default: m.OrderListFinancialApproval,
  })),
);
const OrderPriceConference = lazy(() =>
  import('../pages/OrderPriceConference').then((m) => ({
    default: m.OrderPriceConference,
  })),
);
const OrderMaterialCreate = lazy(() =>
  import('../pages/OrderMaterialCreate').then((m) => ({
    default: m.OrderMaterialCreate,
  })),
);
const OrderMaterialDetails = lazy(() =>
  import('../pages/OrderMaterialDetails').then((m) => ({
    default: m.OrderMaterialDetails,
  })),
);
const OrderMaterialDetailsMobile = lazy(() =>
  import('../pages/OrderMaterialDetailsMobile').then((m) => ({
    default: m.OrderMaterialDetailsMobile,
  })),
);
const OrderMaterialEdit = lazy(() =>
  import('../pages/OrderMaterialEdit').then((m) => ({
    default: m.OrderMaterialEdit,
  })),
);
const OrderMaterialEntries = lazy(() =>
  import('../pages/OrderMaterialEntries').then((m) => ({
    default: m.OrderMaterialEntries,
  })),
);
const OrderMaterialList = lazy(() =>
  import('../pages/OrderMaterialList').then((m) => ({
    default: m.OrderMaterialList,
  })),
);
const OrderMaterialListPending = lazy(() =>
  import('../pages/OrderMaterialListPending').then((m) => ({
    default: m.OrderMaterialListPending,
  })),
);
const OrderPacking = lazy(() =>
  import('../pages/OrderPacking').then((m) => ({ default: m.OrderPacking })),
);
const OrderProduction = lazy(() =>
  import('../pages/OrderProduction').then((m) => ({
    default: m.OrderProduction,
  })),
);
const OrderFirstPiece = lazy(() =>
  import('../pages/OrderProductionApproval').then((m) => ({
    default: m.OrderFirstPiece,
  })),
);
const OrderRate = lazy(() =>
  import('../pages/OrderRate').then((m) => ({ default: m.OrderRate })),
);
const OrderReport = lazy(() =>
  import('../pages/OrderReport').then((m) => ({ default: m.OrderReport })),
);
const OrderScreen = lazy(() =>
  import('../pages/OrderScreen').then((m) => ({ default: m.OrderScreen })),
);
const OrdersXBZ = lazy(() =>
  import('../pages/OrdersXBZ').then((m) => ({ default: m.OrdersXBZ })),
);
const OrderTrack = lazy(() =>
  import('../pages/OrderTrack').then((m) => ({ default: m.OrderTrack })),
);
const OrderTransferList = lazy(() =>
  import('../pages/OrderTransferList').then((m) => ({
    default: m.OrderTransferList,
  })),
);
const OrderUnpacking = lazy(() =>
  import('../pages/OrderUnpacking').then((m) => ({
    default: m.OrderUnpacking,
  })),
);
const OrderWithdrawalCreate = lazy(() =>
  import('../pages/OrderWithdrawalCreate').then((m) => ({
    default: m.OrderWithdrawalCreate,
  })),
);
const OrderWithdrawalDetails = lazy(() =>
  import('../pages/OrderWithdrawalDetails').then((m) => ({
    default: m.OrderWithdrawalDetails,
  })),
);
const OrderWithdrawalList = lazy(() =>
  import('../pages/OrderWithdrawalList').then((m) => ({
    default: m.OrderWithdrawalList,
  })),
);
const PageNotFound = lazy(() =>
  import('../pages/PageNotFound').then((m) => ({ default: m.PageNotFound })),
);
const PaymentReport = lazy(() =>
  import('../pages/PaymentReport').then((m) => ({ default: m.PaymentReport })),
);
const PaymentSlip = lazy(() =>
  import('../pages/PaymentSlip').then((m) => ({ default: m.PaymentSlip })),
);
const PCPList = lazy(() =>
  import('../pages/PCPList').then((m) => ({ default: m.PCPList })),
);
const PostalCreate = lazy(() =>
  import('../pages/PostalCreate').then((m) => ({ default: m.PostalCreate })),
);
const PostalEdit = lazy(() =>
  import('../pages/PostalEdit').then((m) => ({ default: m.PostalEdit })),
);
const PostalList = lazy(() =>
  import('../pages/PostalList').then((m) => ({ default: m.PostalList })),
);
const ProductionReport = lazy(() =>
  import('../pages/ProductionReport').then((m) => ({
    default: m.ProductionReport,
  })),
);
const ProductsXBZ = lazy(() =>
  import('../pages/ProductsXBZ').then((m) => ({ default: m.ProductsXBZ })),
);
const ProductXBZDetails = lazy(() =>
  import('../pages/ProductXBZDetails').then((m) => ({
    default: m.ProductXBZDetails,
  })),
);
const PurchaseCreate = lazy(() =>
  import('../pages/PurchaseCreate').then((m) => ({
    default: m.PurchaseCreate,
  })),
);
const PurchaseEdit = lazy(() =>
  import('../pages/PurchaseEdit').then((m) => ({ default: m.PurchaseEdit })),
);
const PurchaseList = lazy(() =>
  import('../pages/PurchaseList').then((m) => ({ default: m.PurchaseList })),
);
const ReceiptList = lazy(() =>
  import('../pages/ReceiptList').then((m) => ({ default: m.ReceiptList })),
);
const ResetPassword = lazy(() =>
  import('../pages/ResetPassword').then((m) => ({ default: m.ResetPassword })),
);
const SacList = lazy(() =>
  import('../pages/SacList').then((m) => ({ default: m.SacList })),
);
const SacRate = lazy(() =>
  import('../pages/SacRate').then((m) => ({ default: m.SacRate })),
);
const SegmentationTagList = lazy(() =>
  import('../pages/SegmentationTagList').then((m) => ({
    default: m.SegmentationTagList,
  })),
);
const ServiceCheckItemList = lazy(() =>
  import('../pages/ServiceCheckItemList').then((m) => ({
    default: m.ServiceCheckItemList,
  })),
);
const StockCreate = lazy(() =>
  import('../pages/StockCreate').then((m) => ({ default: m.StockCreate })),
);
const StockEdit = lazy(() =>
  import('../pages/StockEdit').then((m) => ({ default: m.StockEdit })),
);
const StockList = lazy(() =>
  import('../pages/StockList').then((m) => ({ default: m.StockList })),
);
const StockRequestList = lazy(() =>
  import('../pages/StockRequestList').then((m) => ({
    default: m.StockRequestList,
  })),
);
const TeamCreate = lazy(() =>
  import('../pages/TeamCreate').then((m) => ({ default: m.TeamCreate })),
);
const TeamEdit = lazy(() =>
  import('../pages/TeamEdit').then((m) => ({ default: m.TeamEdit })),
);
const TeamList = lazy(() =>
  import('../pages/TeamList').then((m) => ({ default: m.TeamList })),
);
const UserCreate = lazy(() =>
  import('../pages/UserCreate').then((m) => ({ default: m.UserCreate })),
);
const UserEdit = lazy(() =>
  import('../pages/UserEdit').then((m) => ({ default: m.UserEdit })),
);
const UserList = lazy(() =>
  import('../pages/UserList').then((m) => ({ default: m.UserList })),
);
const OrderActivitiesSummary = lazy(() =>
  import('../pages/OrderActivitiesSummary').then((m) => ({
    default: m.OrderActivitiesSummary,
  })),
);
const TermDocumentList = lazy(() =>
  import('../pages/TermDocumentList').then((m) => ({
    default: m.TermDocumentList,
  })),
);

export function Router() {
  const signed = useSelector((state: RootState) => state.auth.signed);
  const {
    isAdminType,
    isUserType,
    isUserTypeWithRole,
    isUserTypeWithRoles,
    isMainUser,
  } = useUserConditionalRender();

  return (
    <Suspense fallback={<PageFallback />}>
      <ScrollToTop />
      <Routes>
        {signed && (
          <>
            {isUserTypeWithRoles(
              [
                UserRole.COMPANY,
                UserRole.FINAL_ART,
                UserRole.MANAGER,
                UserRole.OTHER,
                UserRole.PACKER,
                UserRole.RECEPTIONIST,
                UserRole.TECHNICAL,
                UserRole.VENDOR,
                UserRole.FINANCIAL,
                UserRole.HANDLER,
                UserRole.SAC,
              ],
              <>
                <Route path={AppRoutePaths.ORDERS} element={<OrderList />} />
                <Route
                  path={AppRoutePaths.ORDER_HISTORIC}
                  element={<OrderHistoric />}
                />
                {/* 
                <Route
                  path={AppRoutePaths.REPORTS_PRODUCTION}
                  element={<ProductionReport />}
                />
      
                <Route
                  path={AppRoutePaths.REPORTS_HANDLING}
                  element={<HandlingReport />}
                />
                          */}
                <Route
                  path={AppRoutePaths.ORDER_MATERIALS_ENTRIES}
                  element={<OrderMaterialEntries />}
                />
                <Route
                  path={AppRoutePaths.ORDER_TRANSFER}
                  element={<OrderTransferList />}
                />
              </>,
            )}
            <Route path={AppRoutePaths.HOME} element={<Home />} />
            <Route
              path={AppRoutePaths.ORDER_WITHDRAWAL}
              element={<OrderWithdrawalList />}
            />
            <Route
              path={AppRoutePaths.ORDER_WITHDRAWAL_DETAILS}
              element={<OrderWithdrawalDetails />}
            />
            <Route
              path={AppRoutePaths.ORDER_WITHDRAWAL_CREATE}
              element={<OrderWithdrawalCreate />}
            />
            {isAdminType(
              <>
                <Route path={AppRoutePaths.ORDERS} element={<OrderList />} />
                <Route
                  path={AppRoutePaths.BUDGET_LIST}
                  element={<BudgetList />}
                />
                <Route
                  path={AppRoutePaths.EDIT_ORDER}
                  element={<EditOrder />}
                />
                <Route
                  path={AppRoutePaths.ORDER_HISTORIC}
                  element={<OrderHistoric />}
                />
                <Route path={AppRoutePaths.USERS} element={<UserList />} />
                <Route
                  path={AppRoutePaths.USERS_CREATE}
                  element={<UserCreate />}
                />
                <Route path={AppRoutePaths.USERS_EDIT} element={<UserEdit />} />
                <Route path={AppRoutePaths.COMPANY} element={<CompanyList />} />
                <Route
                  path={AppRoutePaths.COMPANY_CREATE}
                  element={<CompanyCreate />}
                />
                <Route
                  path={AppRoutePaths.COMPANY_EDIT}
                  element={<CompanyEdit />}
                />
                <Route path={AppRoutePaths.TEAMS} element={<TeamList />} />
                <Route
                  path={AppRoutePaths.TEAMS_CREATE}
                  element={<TeamCreate />}
                />
                <Route path={AppRoutePaths.TEAMS_EDIT} element={<TeamEdit />} />
                <Route
                  path={AppRoutePaths.REPORTS_PRODUCTION}
                  element={<ProductionReport />}
                />
                <Route
                  path={AppRoutePaths.REPORTS_HANDLING}
                  element={<HandlingReport />}
                />
                <Route
                  path={AppRoutePaths.REPORTS_PAYMENTS}
                  element={<PaymentReport />}
                />
                <Route
                  path={AppRoutePaths.REPORTS_ART}
                  element={<ArtReport />}
                />
                <Route
                  path={AppRoutePaths.ORDER_ACTIVITIES_SUMMARY}
                  element={<OrderActivitiesSummary />}
                />
                <Route
                  path={AppRoutePaths.ORDER_MATERIALS}
                  element={<OrderMaterialList />}
                />
                <Route
                  path={AppRoutePaths.ORDER_MATERIALS_EDIT}
                  element={<OrderMaterialEdit />}
                />
                <Route path={AppRoutePaths.ORDER_PCP} element={<PCPList />} />
                <Route path={AppRoutePaths.SAC_LIST} element={<SacList />} />
                <Route
                  path={AppRoutePaths.ORDERS_LATE}
                  element={<OrderLateList />}
                />
                <Route
                  path={AppRoutePaths.OPTION_LIST}
                  element={<OptionList />}
                />
                <Route
                  path={AppRoutePaths.OPTION_CREATE}
                  element={<CreateOption />}
                />
                <Route
                  path={AppRoutePaths.OPTION_EDIT}
                  element={<EditOption />}
                />
                <Route
                  path={AppRoutePaths.ORDER_MATERIALS_PENDING}
                  element={<OrderMaterialListPending />}
                />
                <Route
                  path={AppRoutePaths.ORDERS_FIRST_CONTACT}
                  element={<OrderFirstContact />}
                />
                <Route
                  path={AppRoutePaths.PAYMENT_SLIP_LIST}
                  element={<PaymentSlip />}
                />
                <Route
                  path={AppRoutePaths.ORDER_MATERIALS_ENTRIES}
                  element={<OrderMaterialEntries />}
                />
                <Route
                  path={AppRoutePaths.SERVICE_ITEM_LIST}
                  element={<ServiceCheckItemList />}
                />
                <Route
                  path={AppRoutePaths.FINANCIAL_PAYMENT}
                  element={<FinancialPayment />}
                />
                <Route
                  path={AppRoutePaths.SEGMENTATION_TAG_LIST}
                  element={<SegmentationTagList />}
                />
                <Route
                  path={AppRoutePaths.ORDER_APPROVALS}
                  element={<OrderChangeRequests />}
                />
                <Route
                  path={AppRoutePaths.ORDER_EVENT_EDIT}
                  element={<OrderEventEdit />}
                />
                <Route
                  path={AppRoutePaths.STOCK_LIST}
                  element={<StockList />}
                />
                <Route
                  path={AppRoutePaths.STOCK_REQUEST_LIST}
                  element={<StockRequestList />}
                />
                <Route
                  path={AppRoutePaths.STOCK_CREATE}
                  element={<StockCreate />}
                />
                <Route
                  path={AppRoutePaths.STOCK_EDIT}
                  element={<StockEdit />}
                />
                <Route
                  path={AppRoutePaths.FINANCIAL_CHECK}
                  element={<OrderListFinancialApproval />}
                />
                <Route
                  path={AppRoutePaths.REPORTS_EVENTS}
                  element={<EventReport />}
                />
                <Route
                  path={AppRoutePaths.REPORT_ORDERS}
                  element={<OrderReport />}
                />
                <Route
                  path={AppRoutePaths.FINANCIAL_NF_CONTROL}
                  element={<FinancialNFControl />}
                />
                <Route
                  path={AppRoutePaths.ORDERS_XBZ}
                  element={<OrdersXBZ />}
                />
                <Route
                  path={AppRoutePaths.PRODUCTS_XBZ}
                  element={<ProductsXBZ />}
                />
                <Route
                  path={AppRoutePaths.PRODUCT_XBZ_DETAILS}
                  element={<ProductXBZDetails />}
                />
                <Route
                  path={AppRoutePaths.COMPANY_XBZ}
                  element={<CompanyXBZList />}
                />
                <Route
                  path={AppRoutePaths.CREATE_ORDERS_XBZ}
                  element={<OrderCreateByXBZ />}
                />
                <Route
                  path={AppRoutePaths.CREATE_ORDERS_KIT}
                  element={<OrderCreateKit />}
                />
                <Route
                  path={AppRoutePaths.CREATE_ORDER}
                  element={<CreateOrder />}
                />
                <Route
                  path={AppRoutePaths.CHAT_MONITOR}
                  element={<ChatMonitor />}
                />
                <Route
                  path={AppRoutePaths.ORDER_TRANSFER}
                  element={<OrderTransferList />}
                />
                <Route
                  path={AppRoutePaths.PRICE_CONFERENCE}
                  element={<OrderPriceConference />}
                />
                <Route
                  path={AppRoutePaths.ORDER_EVENT_CREATE}
                  element={<OrderEventCreate />}
                />
                <Route
                  path={AppRoutePaths.TERM_DOCUMENTS}
                  element={<TermDocumentList />}
                />
                <Route
                  path={AppRoutePaths.CONFIRM_BUDGET}
                  element={<ConfirmBudget />}
                />
                <Route
                  path={AppRoutePaths.ACTIVITIES}
                  element={<ActivityList />}
                />
                <Route
                  path={AppRoutePaths.ACTIVITY_LAYOUTS}
                  element={<LayoutList />}
                />
                <Route
                  path={AppRoutePaths.POSTAL_LIST}
                  element={<PostalList />}
                />
                <Route
                  path={AppRoutePaths.POSTAL_CREATE}
                  element={<PostalCreate />}
                />
                <Route
                  path={AppRoutePaths.POSTAL_EDIT}
                  element={<PostalEdit />}
                />
                <Route
                  path={AppRoutePaths.ART_ACTIVITY}
                  element={<ArtProduction />}
                />
                <Route
                  path={AppRoutePaths.ORDERS_FIRST_PIECE}
                  element={<OrderFirstPiece />}
                />
                <Route
                  path={AppRoutePaths.ORDER_TRACK}
                  element={<OrderTrack />}
                />
                <Route
                  path={AppRoutePaths.ORDER_EVENT_LIST}
                  element={<OrderEventList />}
                />
                <Route
                  path={AppRoutePaths.ORDER_PRODUCTION}
                  element={<OrderProduction />}
                />
                <Route
                  path={AppRoutePaths.ORDER_SCREEN}
                  element={<OrderScreen />}
                />
                <Route
                  path={AppRoutePaths.ORDER_HANDLING}
                  element={<OrderHandling />}
                />
                <Route
                  path={AppRoutePaths.ORDER_DEFECT}
                  element={<OrderDefect />}
                />
                <Route
                  path={AppRoutePaths.ACTIVITIES_EXPORT}
                  element={<ActivityListExport />}
                />
                <Route
                  path={AppRoutePaths.ORDER_PACKING}
                  element={<OrderPacking />}
                />
                <Route
                  path={AppRoutePaths.ORDER_UNPACKING}
                  element={<OrderUnpacking />}
                />
                <Route
                  path={AppRoutePaths.ORDER_MATERIALS_CREATE}
                  element={<OrderMaterialCreate />}
                />
                <Route
                  path={AppRoutePaths.RECEIPT_LIST}
                  element={<ReceiptList />}
                />
                <Route
                  path={AppRoutePaths.FINANCIAL_INVOICE}
                  element={<FinancialInvoice />}
                />
                <Route
                  path={AppRoutePaths.PURCHASES}
                  element={<PurchaseList />}
                />
                <Route
                  path={AppRoutePaths.PURCHASES_CREATE}
                  element={<PurchaseCreate />}
                />
                <Route
                  path={AppRoutePaths.PURCHASES_EDIT}
                  element={<PurchaseEdit />}
                />
                <Route path={AppRoutePaths.BILLS} element={<BillList />} />
                <Route
                  path={AppRoutePaths.BILLS_CREATE}
                  element={<BillCreate />}
                />
                <Route path={AppRoutePaths.BILLS_EDIT} element={<BillEdit />} />
                <Route
                  path={AppRoutePaths.ORDER_FILE_RECEIPTS_APPROVAL}
                  element={<OrderFileReceiptsApproval />}
                />
              </>,
            )}
            {isUserType(
              <>
                <Route
                  path={AppRoutePaths.CONFIRM_BUDGET}
                  element={<ConfirmBudget />}
                />
                <Route
                  path={AppRoutePaths.CREATE_ORDER}
                  element={<CreateOrder />}
                />
                <Route
                  path={AppRoutePaths.EDIT_ORDER}
                  element={<EditOrder />}
                />
              </>,
            )}
            {isUserTypeWithRole(
              UserRole.VENDOR,
              <>
                <Route path={AppRoutePaths.COMPANY} element={<CompanyList />} />
                <Route
                  path={AppRoutePaths.COMPANY_CREATE}
                  element={<CompanyCreate />}
                />
                <Route
                  path={AppRoutePaths.COMPANY_EDIT}
                  element={<CompanyEdit />}
                />
                <Route
                  path={AppRoutePaths.ACTIVITIES}
                  element={<ActivityList />}
                />
                <Route
                  path={AppRoutePaths.ORDER_MATERIALS}
                  element={<OrderMaterialList />}
                />
                <Route
                  path={AppRoutePaths.ACTIVITY_LAYOUTS}
                  element={<LayoutList />}
                />
                <Route
                  path={AppRoutePaths.POSTAL_LIST}
                  element={<PostalList />}
                />
                <Route
                  path={AppRoutePaths.POSTAL_CREATE}
                  element={<PostalCreate />}
                />
                <Route
                  path={AppRoutePaths.POSTAL_EDIT}
                  element={<PostalEdit />}
                />
                <Route
                  path={AppRoutePaths.ART_ACTIVITY}
                  element={<ArtProduction />}
                />
                <Route
                  path={AppRoutePaths.REPORTS_PAYMENTS}
                  element={<PaymentReport />}
                />
                <Route path={AppRoutePaths.ORDER_PCP} element={<PCPList />} />
                <Route path={AppRoutePaths.SAC_LIST} element={<SacList />} />
                <Route
                  path={AppRoutePaths.ORDERS_LATE}
                  element={<OrderLateList />}
                />
                <Route
                  path={AppRoutePaths.PAYMENT_SLIP_LIST}
                  element={<PaymentSlip />}
                />
                <Route
                  path={AppRoutePaths.ORDERS_FIRST_CONTACT}
                  element={<OrderFirstContact />}
                />
                <Route
                  path={AppRoutePaths.ORDERS_FIRST_PIECE}
                  element={<OrderFirstPiece />}
                />
                <Route
                  path={AppRoutePaths.SEGMENTATION_TAG_LIST}
                  element={<SegmentationTagList />}
                />
                <Route
                  path={AppRoutePaths.ORDER_EVENT_CREATE}
                  element={<OrderEventCreate />}
                />
                <Route
                  path={AppRoutePaths.ORDER_EVENT_EDIT}
                  element={<OrderEventEdit />}
                />
                <Route
                  path={AppRoutePaths.STOCK_REQUEST_LIST}
                  element={<StockRequestList />}
                />
                <Route
                  path={AppRoutePaths.ORDER_TRACK}
                  element={<OrderTrack />}
                />
                <Route
                  path={AppRoutePaths.ORDER_EVENT_LIST}
                  element={<OrderEventList />}
                />
                <Route
                  path={AppRoutePaths.REPORTS_EVENTS}
                  element={<EventReport />}
                />
                <Route
                  path={AppRoutePaths.REPORT_ORDERS}
                  element={<OrderReport />}
                />
                <Route
                  path={AppRoutePaths.ORDERS_XBZ}
                  element={<OrdersXBZ />}
                />
                <Route
                  path={AppRoutePaths.ORDER_MATERIALS_PENDING}
                  element={<OrderMaterialListPending />}
                />
                <Route
                  path={AppRoutePaths.CREATE_ORDERS_XBZ}
                  element={<OrderCreateByXBZ />}
                />
                <Route
                  path={AppRoutePaths.CREATE_ORDERS_KIT}
                  element={<OrderCreateKit />}
                />
                <Route
                  path={AppRoutePaths.RECEIPT_LIST}
                  element={<ReceiptList />}
                />
                {isMainUser(
                  <>
                    <Route path={AppRoutePaths.USERS} element={<UserList />} />
                    <Route
                      path={AppRoutePaths.USERS_CREATE}
                      element={<UserCreate />}
                    />
                    <Route
                      path={AppRoutePaths.USERS_EDIT}
                      element={<UserEdit />}
                    />
                    <Route
                      path={AppRoutePaths.COMPANY_EDIT}
                      element={<CompanyEdit />}
                    />
                  </>,
                )}
              </>,
            )}
            {isUserTypeWithRole(
              UserRole.TECHNICAL,
              <>
                <Route
                  path={AppRoutePaths.ORDER_PRODUCTION}
                  element={<OrderProduction />}
                />
                <Route
                  path={AppRoutePaths.ORDER_SCREEN}
                  element={<OrderScreen />}
                />
                <Route path={AppRoutePaths.ORDER_PCP} element={<PCPList />} />
                <Route
                  path={AppRoutePaths.STOCK_REQUEST_LIST}
                  element={<StockRequestList />}
                />
                <Route
                  path={AppRoutePaths.ORDER_HANDLING}
                  element={<OrderHandling />}
                />
                <Route
                  path={AppRoutePaths.ORDERS_XBZ}
                  element={<OrdersXBZ />}
                />
              </>,
            )}
            {isUserTypeWithRole(
              UserRole.COMPANY,
              <>
                <Route
                  path={AppRoutePaths.ACTIVITY_LAYOUTS}
                  element={<LayoutList />}
                />
                <Route path={AppRoutePaths.SAC_LIST} element={<SacList />} />
                <Route
                  path={AppRoutePaths.ORDER_DEFECT}
                  element={<OrderDefect />}
                />
                <Route
                  path={AppRoutePaths.PAYMENT_SLIP_LIST}
                  element={<PaymentSlip />}
                />
                <Route
                  path={AppRoutePaths.ORDER_EVENT_CREATE}
                  element={<OrderEventCreate />}
                />
                <Route
                  path={AppRoutePaths.ORDER_EVENT_EDIT}
                  element={<OrderEventEdit />}
                />
                <Route
                  path={AppRoutePaths.ORDER_TRACK}
                  element={<OrderTrack />}
                />
                <Route
                  path={AppRoutePaths.ORDERS_XBZ}
                  element={<OrdersXBZ />}
                />
                <Route
                  path={AppRoutePaths.CREATE_ORDERS_XBZ}
                  element={<OrderCreateByXBZ />}
                />
                <Route
                  path={AppRoutePaths.CREATE_ORDERS_KIT}
                  element={<OrderCreateKit />}
                />
                <Route
                  path={AppRoutePaths.PRODUCTS_XBZ}
                  element={<ProductsXBZ />}
                />
                <Route
                  path={AppRoutePaths.PRODUCT_XBZ_DETAILS}
                  element={<ProductXBZDetails />}
                />
              </>,
            )}
            {isUserTypeWithRole(
              UserRole.FINAL_ART,
              <>
                <Route
                  path={AppRoutePaths.ACTIVITIES}
                  element={<ActivityList />}
                />
                <Route
                  path={AppRoutePaths.ART_ACTIVITY}
                  element={<ArtProduction />}
                />
                <Route
                  path={AppRoutePaths.STOCK_REQUEST_LIST}
                  element={<StockRequestList />}
                />
                <Route
                  path={AppRoutePaths.ORDERS_XBZ}
                  element={<OrdersXBZ />}
                />
                <Route
                  path={AppRoutePaths.ACTIVITIES_EXPORT}
                  element={<ActivityListExport />}
                />
                <Route
                  path={AppRoutePaths.REPORTS_ART}
                  element={<ArtReport />}
                />
              </>,
            )}
            {isUserTypeWithRole(
              UserRole.PACKER,
              <>
                <Route
                  path={AppRoutePaths.ORDER_PACKING}
                  element={<OrderPacking />}
                />
                <Route
                  path={AppRoutePaths.ORDER_UNPACKING}
                  element={<OrderUnpacking />}
                />
                <Route
                  path={AppRoutePaths.STOCK_REQUEST_LIST}
                  element={<StockRequestList />}
                />
                <Route path={AppRoutePaths.ORDER_PCP} element={<PCPList />} />
                <Route
                  path={AppRoutePaths.ORDERS_XBZ}
                  element={<OrdersXBZ />}
                />
              </>,
            )}
            {isUserTypeWithRole(
              UserRole.HANDLER,
              <>
                <Route
                  path={AppRoutePaths.ORDER_HANDLING}
                  element={<OrderHandling />}
                />
                <Route
                  path={AppRoutePaths.STOCK_REQUEST_LIST}
                  element={<StockRequestList />}
                />
                <Route
                  path={AppRoutePaths.ORDERS_XBZ}
                  element={<OrdersXBZ />}
                />
              </>,
            )}
            {isUserTypeWithRole(
              UserRole.RECEPTIONIST,
              <>
                <Route
                  path={AppRoutePaths.ORDER_MATERIALS}
                  element={<OrderMaterialList />}
                />
                <Route
                  path={AppRoutePaths.ORDER_MATERIALS_CREATE}
                  element={<OrderMaterialCreate />}
                />
                <Route
                  path={AppRoutePaths.ORDER_MATERIALS_EDIT}
                  element={<OrderMaterialEdit />}
                />
                <Route
                  path={AppRoutePaths.POSTAL_LIST}
                  element={<PostalList />}
                />
                <Route
                  path={AppRoutePaths.POSTAL_CREATE}
                  element={<PostalCreate />}
                />
                <Route
                  path={AppRoutePaths.POSTAL_EDIT}
                  element={<PostalEdit />}
                />
                <Route path={AppRoutePaths.COMPANY} element={<CompanyList />} />
                <Route
                  path={AppRoutePaths.RECEIPT_LIST}
                  element={<ReceiptList />}
                />
                <Route
                  path={AppRoutePaths.STOCK_REQUEST_LIST}
                  element={<StockRequestList />}
                />
                {isMainUser(
                  <>
                    <Route
                      path={AppRoutePaths.COMPANY_EDIT}
                      element={<CompanyEdit />}
                    />
                  </>,
                )}
                <Route
                  path={AppRoutePaths.ORDER_MATERIALS_PENDING}
                  element={<OrderMaterialListPending />}
                />
                <Route
                  path={AppRoutePaths.PAYMENT_SLIP_LIST}
                  element={<PaymentSlip />}
                />
                <Route
                  path={AppRoutePaths.ORDERS_XBZ}
                  element={<OrdersXBZ />}
                />
              </>,
            )}
            {isUserTypeWithRole(
              UserRole.FINANCIAL,
              <>
                <Route
                  path={AppRoutePaths.FINANCIAL_INVOICE}
                  element={<FinancialInvoice />}
                />
                <Route
                  path={AppRoutePaths.FINANCIAL_PAYMENT}
                  element={<FinancialPayment />}
                />
                <Route
                  path={AppRoutePaths.PURCHASES}
                  element={<PurchaseList />}
                />
                <Route
                  path={AppRoutePaths.PURCHASES_CREATE}
                  element={<PurchaseCreate />}
                />
                <Route
                  path={AppRoutePaths.PURCHASES_EDIT}
                  element={<PurchaseEdit />}
                />
                <Route path={AppRoutePaths.BILLS} element={<BillList />} />
                <Route
                  path={AppRoutePaths.BILLS_CREATE}
                  element={<BillCreate />}
                />
                <Route path={AppRoutePaths.COMPANY} element={<CompanyList />} />
                <Route
                  path={AppRoutePaths.COMPANY_CREATE}
                  element={<CompanyCreate />}
                />
                <Route
                  path={AppRoutePaths.COMPANY_EDIT}
                  element={<CompanyEdit />}
                />
                <Route path={AppRoutePaths.BILLS_EDIT} element={<BillEdit />} />
                <Route
                  path={AppRoutePaths.REPORTS_ART}
                  element={<ArtReport />}
                />
                <Route
                  path={AppRoutePaths.ORDER_ACTIVITIES_SUMMARY}
                  element={<OrderActivitiesSummary />}
                />
                <Route
                  path={AppRoutePaths.REPORTS_PAYMENTS}
                  element={<PaymentReport />}
                />
                <Route
                  path={AppRoutePaths.POSTAL_LIST}
                  element={<PostalList />}
                />
                <Route
                  path={AppRoutePaths.POSTAL_CREATE}
                  element={<PostalCreate />}
                />
                <Route
                  path={AppRoutePaths.POSTAL_EDIT}
                  element={<PostalEdit />}
                />
                <Route
                  path={AppRoutePaths.RECEIPT_LIST}
                  element={<ReceiptList />}
                />
                <Route
                  path={AppRoutePaths.ORDER_FILE_RECEIPTS_APPROVAL}
                  element={<OrderFileReceiptsApproval />}
                />
                <Route
                  path={AppRoutePaths.PAYMENT_SLIP_LIST}
                  element={<PaymentSlip />}
                />
                <Route
                  path={AppRoutePaths.ORDERS_LATE}
                  element={<OrderLateList />}
                />
                <Route path={AppRoutePaths.SAC_LIST} element={<SacList />} />
                <Route
                  path={AppRoutePaths.STOCK_REQUEST_LIST}
                  element={<StockRequestList />}
                />
                <Route
                  path={AppRoutePaths.FINANCIAL_CHECK}
                  element={<OrderListFinancialApproval />}
                />
                <Route
                  path={AppRoutePaths.PRICE_CONFERENCE}
                  element={<OrderPriceConference />}
                />
                <Route
                  path={AppRoutePaths.FINANCIAL_NF_CONTROL}
                  element={<FinancialNFControl />}
                />
                <Route
                  path={AppRoutePaths.ORDERS_XBZ}
                  element={<OrdersXBZ />}
                />
              </>,
            )}
            {isUserTypeWithRole(
              UserRole.WAREHOUSE,
              <>
                <Route
                  path={AppRoutePaths.STOCK_LIST}
                  element={<StockList />}
                />
                <Route
                  path={AppRoutePaths.STOCK_REQUEST_LIST}
                  element={<StockRequestList />}
                />
                <Route
                  path={AppRoutePaths.STOCK_CREATE}
                  element={<StockCreate />}
                />
                <Route
                  path={AppRoutePaths.STOCK_EDIT}
                  element={<StockEdit />}
                />
              </>,
            )}
            {isUserTypeWithRole(
              UserRole.SCREEN,
              <>
                <Route path={AppRoutePaths.ORDERS} element={<OrderList />} />
                <Route
                  path={AppRoutePaths.ORDER_SCREEN}
                  element={<OrderScreen />}
                />
              </>,
            )}
            {isUserTypeWithRole(
              UserRole.SAC,
              <>
                <Route path={AppRoutePaths.COMPANY} element={<CompanyList />} />
                <Route
                  path={AppRoutePaths.ORDER_MATERIALS}
                  element={<OrderMaterialList />}
                />
                <Route
                  path={AppRoutePaths.POSTAL_LIST}
                  element={<PostalList />}
                />
                <Route
                  path={AppRoutePaths.POSTAL_CREATE}
                  element={<PostalCreate />}
                />
                <Route
                  path={AppRoutePaths.POSTAL_EDIT}
                  element={<PostalEdit />}
                />
                <Route path={AppRoutePaths.SAC_LIST} element={<SacList />} />
                <Route
                  path={AppRoutePaths.ORDERS_LATE}
                  element={<OrderLateList />}
                />
                <Route
                  path={AppRoutePaths.ORDER_TRACK}
                  element={<OrderTrack />}
                />
                <Route
                  path={AppRoutePaths.ORDER_EVENT_LIST}
                  element={<OrderEventList />}
                />
                <Route
                  path={AppRoutePaths.REPORT_ORDERS}
                  element={<OrderReport />}
                />
                <Route
                  path={AppRoutePaths.ORDERS_XBZ}
                  element={<OrdersXBZ />}
                />
                <Route
                  path={AppRoutePaths.ORDER_MATERIALS_PENDING}
                  element={<OrderMaterialListPending />}
                />
              </>,
            )}
            <Route
              path={AppRoutePaths.ORDER_MATERIALS_DETAILS}
              element={<OrderMaterialDetails />}
            />
          </>
        )}
        <Route path={AppRoutePaths.LOGIN} element={<Login />} />
        <Route path={AppRoutePaths.NOT_FOUND} element={<PageNotFound />} />
        <Route
          path={AppRoutePaths.RESET_PASSWORD}
          element={<ResetPassword />}
        />
        <Route
          path={AppRoutePaths.ORDER_DISPATCH}
          element={<OrderDispatch />}
        />
        <Route path={AppRoutePaths.SAC_RATE} element={<SacRate />} />
        <Route
          path={AppRoutePaths.CLIENT_ORDER_CANCEL}
          element={<OrderCancel />}
        />
        <Route
          path={AppRoutePaths.CLIENT_ORDER_APPROVAL_DEADLINE}
          element={<OrderApprovalDeadline />}
        />
        <Route path={AppRoutePaths.ORDER_RATE} element={<OrderRate />} />
        <Route
          path={AppRoutePaths.ORDER_MATERIAL_MOBILE}
          element={<OrderMaterialDetailsMobile />}
        />
      </Routes>
    </Suspense>
  );
}
